import { useEffect, useId, useRef, useState } from "react";
import { Check, ChevronDown, ChevronUp, Copy, RotateCcw } from "lucide-react";

import { StreamErrorDetails } from "@/api/models";
import { getErrorDetails } from "@/utils/errorMessages";

// Human-readable label per normalized reason code from the backend
// (STREAM_EMPTY_REASON_MESSAGES in backend/utils/utilities.py). Unknown codes
// fall back to the raw code so a newly added reason still renders something
// useful instead of blank.
const REASON_LABELS: Record<string, string> = {
  max_output_tokens: "Output token budget exhausted",
  content_filter: "Blocked by content filter",
  content_filter_prompt: "Prompt blocked by content filter",
  deployment_unavailable: "Model deployment unavailable",
  refusal: "Model refused the request",
  tool_calls: "Unsupported tool call",
  capacity: "Model at capacity",
  upstream_failed: "Upstream provider failed",
  empty_response: "Empty response from model",
  file_workspace_capacity: "File workspace at capacity",
  file_workspace_unavailable: "File workspace unavailable",
  context_length_exceeded: "Context window exceeded",
  provider_auth: "Provider authentication failed",
  upstream_timeout: "Model timed out",
  upstream_unreachable: "Model provider unreachable",
  invalid_prompt: "Request rejected as invalid",
};

type ErrorDetailsProps = {
  details?: StreamErrorDetails;
  error?: unknown;
  statusCode?: number;
  onRetry?: () => void | Promise<void>;
  retryBusy?: boolean;
};

const ErrorDetails = ({ details, error, statusCode, onRetry, retryBusy = false }: ErrorDetailsProps) => {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const retryInFlightRef = useRef(false);
  const panelId = useId();

  const resolvedDetails = getErrorDetails(error, details, statusCode);

  useEffect(() => () => clearTimeout(copiedTimerRef.current), []);

  const rows: [string, string][] = [
    ["Reason", resolvedDetails.reason && resolvedDetails.reason !== "unknown"
      ? REASON_LABELS[resolvedDetails.reason] || resolvedDetails.reason
      : ""],
    ["Error type", resolvedDetails.error_type || ""],
    ["Error code", resolvedDetails.error_code || ""],
    ["HTTP status", resolvedDetails.status_code ? String(resolvedDetails.status_code) : ""],
    ["Model", resolvedDetails.model || ""],
    ["Reasoning", resolvedDetails.reasoning || ""],
    ["Trace ID", resolvedDetails.trace_id || ""],
    ["Conversation", resolvedDetails.conversation_id || ""],
    ["Upstream ID", resolvedDetails.upstream_id || ""],
    ["Upstream code", resolvedDetails.upstream_code || ""],
    ["Upstream status", resolvedDetails.upstream_status_code ? String(resolvedDetails.upstream_status_code) : ""],
    ["Upstream detail", resolvedDetails.upstream_detail || ""],
    ["Time", resolvedDetails.time || ""],
  ];

  const populatedRows = rows.filter(([, value]) => value !== "");
  const hasDetails = populatedRows.length > 0;

  if (!hasDetails && !onRetry) return null;

  const isRetryPending = retryBusy || isRetrying;
  const handleRetry = async () => {
    if (!onRetry || retryInFlightRef.current || isRetryPending) return;

    retryInFlightRef.current = true;
    setIsRetrying(true);
    try {
      await onRetry();
    } catch (retryError) {
      console.error("Failed to retry response:", retryError);
    } finally {
      retryInFlightRef.current = false;
      setIsRetrying(false);
    }
  };

  const copyDetails = async () => {
    try {
      await navigator.clipboard.writeText(
        populatedRows.map(([label, value]) => `${label}: ${value}`).join("\n")
      );
      setCopied(true);
      clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be blocked by permissions; the text is on screen anyway.
    }
  };

  return (
    <div className={onRetry ? "mt-4" : "mt-1"}>
      <div className={onRetry ? "flex items-center justify-between gap-3" : ""}>
        {onRetry && (
          <button
            type="button"
            className="inline-flex min-h-0 items-center gap-1 border-0 bg-transparent p-0 ps-2 text-xs font-medium  hover:bg-transparent hover:underline disabled:cursor-wait disabled:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current"
            onClick={handleRetry}
            disabled={isRetryPending}
          >
            <RotateCcw size={14} className={isRetryPending ? "animate-spin" : ""} aria-hidden="true" />
            <span aria-live="polite">{isRetryPending ? "Trying again…" : "Try again"}</span>
          </button>
        )}

        {hasDetails && (
          <button
            type="button"
            className="inline-flex min-h-0 items-center gap-1 border-0 bg-transparent p-0 text-[11px] underline underline-offset-2 opacity-65 hover:bg-transparent hover:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current"
            onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded}
            aria-controls={panelId}
          >
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {expanded ? "Hide details" : "View details"}
          </button>
        )}
      </div>

      {expanded && hasDetails && (
        <div id={panelId} className="mt-1 font-mono">
          <dl className="space-y-1 text-[11px] font-mono">
            {populatedRows.map(([label, value]) => (
              <div key={label} className="flex gap-2">
                <dt className="w-28 shrink-0 opacity-60">{label}</dt>
                <dd className="min-w-0 flex-1 whitespace-pre-wrap break-words">{value}</dd>
              </div>
            ))}
          </dl>
          <button
            type="button"
            className="mt-2 inline-flex min-h-0 items-center gap-1 border-0 bg-transparent p-0 text-[11px] underline underline-offset-2 opacity-65 hover:bg-transparent hover:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current"
            onClick={copyDetails}
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? "Copied" : "Copy details"}
          </button>
        </div>
      )}
    </div>
  );
};

export default ErrorDetails;
